import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AgGridAngular } from 'ag-grid-angular';
import { NumberFilterModule, themeQuartz } from 'ag-grid-community';
import type { ColDef, Module } from 'ag-grid-community';
import type { RowObject } from '@highcharts/dashboards/es-modules/Data/DataTable.js';
import type { DashboardQueryColumn } from '@h20/contracts/types/api/dashboard/DashboardData';
import { DASHBOARD_QUERY_CATALOG } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import { DashboardWidget, pickedColumnName } from '@h20-shared/dashboards';
import type { GridContext } from '@h20-shared/grids/grid-context';
import { PulseSegmentedToggle } from '@h20-shared/pulse-components/pulse-segmented-toggle';
import { getGlobalAgGridModules } from '@h20-shared/table-component/global-ag-grid-module-manager';
import { FALLBACK_LOCALE } from '@h20-shared/utils/number-format-utils';
import { buildViewRows } from './dashboard-grid.group-by';
import type { GridViewConfig, GridWidgetConfig } from './dashboard-grid.schema';
import { buildGridColumns, buildMainValueColumn } from './columns';

/** One button in the view switch above the table. */
type GridViewOption = {
    readonly id: string;
    readonly label: string;
    readonly view: GridViewConfig;
};

/** Shows a catalogue query as a table, with each column styled by the registry or by its type. */
@Component({
    selector: 'pulse-dashboard-grid',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AgGridAngular, TranslateModule, ...PulseSegmentedToggle],
    templateUrl: './dashboard-grid.widget.html',
    styleUrl: './dashboard-grid.widget.scss',
})
export class DashboardGridWidget extends DashboardWidget<RowObject, GridWidgetConfig> {
    private readonly translate = inject(TranslateService);
    private readonly langChange = toSignal(this.translate.onLangChange);

    protected readonly standardTheme = themeQuartz;
    protected readonly pageSize = 25;
    protected readonly modules: Module[] = [...getGlobalAgGridModules(), NumberFilterModule];

    /** The buttons of the view switch. Empty while grouping is off, which hides the switch. */
    protected readonly viewOptions = computed<GridViewOption[]>(() => {
        this.langChange();
        const config = this.config();
        if (config === undefined || !config.grouping.enabled) {
            return [];
        }
        return Object.entries(config.grouping.views.items).map(([id, view], index) => ({
            id,
            label:
                view.label ||
                this.translate.instant('Dashboard.Grid.UntitledView', { number: index + 1 }),
            view,
        }));
    });

    /** The selected view */
    protected readonly selectedViewId = linkedSignal<readonly GridViewOption[], string>({
        source: this.viewOptions,
        computation: (options, previous) => {
            const chosen = previous?.value;
            if (chosen !== undefined && options.some((option) => option.id === chosen)) {
                return chosen;
            }
            return options[0]?.id ?? '';
        },
    });

    protected readonly selectedView = computed<GridViewConfig | undefined>(() => {
        const id = this.selectedViewId();
        return this.viewOptions().find((option) => option.id === id)?.view;
    });

    protected readonly switchHeading = computed<string>(() => {
        this.langChange();
        return (
            this.config()?.grouping.switchHeading || this.translate.instant('Dashboard.Grid.ViewBy')
        );
    });

    /** The keys the rows carry, or none when there are no rows to read them from. */
    private readonly presentKeys = computed<readonly string[] | undefined>(
        () => {
            const firstRow = this.rows()[0];
            return firstRow === undefined ? undefined : Object.keys(firstRow);
        },
        {
            equal: (left, right) => {
                if (left === undefined || right === undefined) {
                    return left === right;
                }
                return (
                    left.length === right.length && left.every((key, index) => key === right[index])
                );
            },
        }
    );

    /** The column the selected view folds rows by, or `undefined` to leave every row on its own. */
    private readonly groupColumn = computed<string | undefined>(() => {
        const view = this.selectedView();
        const column = view === undefined ? undefined : pickedColumnName(view.column);
        if (column === undefined) {
            return undefined;
        }
        const presentKeys = this.presentKeys();
        return presentKeys === undefined || presentKeys.includes(column) ? column : undefined;
    });

    private readonly summedColumns = computed<readonly string[]>(() => {
        const queryKey = this.queryKey();
        if (queryKey === undefined) {
            return [];
        }
        const columns: readonly DashboardQueryColumn[] = DASHBOARD_QUERY_CATALOG[queryKey].columns;
        return columns
            .filter((column) => column.sumWhenGrouped === true)
            .map((column) => column.key);
    });

    protected readonly rowData = computed<RowObject[]>(() => {
        const rows = this.rows();
        const config = this.config();
        if (this.selectedView() === undefined || config === undefined) {
            return [...rows];
        }
        return buildViewRows(
            rows,
            this.groupColumn(),
            config.grouping.mainValue,
            this.summedColumns()
        );
    });

    /** Paging a table that fits on one page only costs it a footer. */
    protected readonly paginate = computed<boolean>(() => this.rowData().length > this.pageSize);

    protected readonly noRowsTemplate = computed<string>(
        () => `<span>${this.translate.instant('Dashboard.Grid.Empty')}</span>`
    );

    /** What every column gets unless its column file says otherwise. */
    protected readonly defaultColDef: ColDef<RowObject> = {
        flex: 1,
        minWidth: 100,
        resizable: false,
        suppressMovable: true,
        unSortIcon: true,
        wrapText: true,
        autoHeight: true,
        filterParams: { buttons: ['reset', 'apply'], closeOnApply: true },
    };

    /** Handed to every column callback, so a formatter can write numbers in the reader's language. */
    protected readonly gridContext = computed<GridContext>(() => {
        this.langChange();
        return { locale: this.translate.currentLang || FALLBACK_LOCALE };
    });

    /** The pagination footer words, from the same keys `TableComponent` uses. */
    protected readonly localeText = computed(() => {
        this.langChange();
        return {
            page: this.translate.instant('PatientView.Page'),
            of: this.translate.instant('PatientView.Of'),
            to: this.translate.instant('PatientView.To'),
        };
    });

    protected readonly columnDefs = computed<ColDef<RowObject>[]>(() => {
        // Headers are translated and cells formatted in the current language, so a language
        // change rebuilds the columns, which is also what makes ag-grid redraw every cell.
        this.langChange();
        const queryKey = this.queryKey();
        if (queryKey === undefined) {
            return [];
        }
        const translate = (i18nKey: string): string => this.translate.instant(i18nKey);
        const allColumns = buildGridColumns(queryKey, this.presentKeys(), translate);

        const config = this.config();
        const view = this.selectedView();
        const groupColumn = this.groupColumn();

        const visible = allColumns.filter((column) => {
            const key = column.colId;
            if (key === undefined || key === groupColumn) {
                return true;
            }
            // Only an explicit `true` hides a column
            return config?.columns[key]?.hidden !== true && view?.columns[key] !== true;
        });

        let mainValueColumn: ColDef<RowObject> | undefined;
        if (view !== undefined && config !== undefined) {
            mainValueColumn = buildMainValueColumn(config.grouping.mainValue, translate);
        }
        const mainValueColumns = mainValueColumn === undefined ? [] : [mainValueColumn];

        // Without typed positions the table shows the group column, then the main value, then the other columns in catalogue order.
        const drawn = visible.filter((column) => column.hide !== true);
        const groupColumns = drawn.filter((column) => column.colId === groupColumn);
        const otherColumns = drawn.filter((column) => column.colId !== groupColumn);
        const layout = [...groupColumns, ...mainValueColumns, ...otherColumns];

        // Each typed column then goes to its place in the drawn table, counted from 1, and the rest
        // keep their order around it. A number past the end puts the column last, and a repeated
        // number takes the next place.
        const typedColumns: { column: ColDef<RowObject>; position: number }[] = [];
        const orderedColumns: ColDef<RowObject>[] = [];
        for (const column of layout) {
            let savedPosition = config?.columns[column.colId ?? '']?.position;
            if (column === mainValueColumn) {
                savedPosition = config?.grouping.mainValue.position;
            }
            const positionText = (savedPosition ?? '').trim();
            const position = Number(positionText);
            if (positionText === '' || !Number.isInteger(position)) {
                orderedColumns.push(column);
            } else {
                typedColumns.push({ column, position });
            }
        }
        typedColumns.sort((first, second) => first.position - second.position);
        let placedIndex = -1;
        for (const { column, position } of typedColumns) {
            placedIndex = Math.max(position - 1, placedIndex + 1);
            orderedColumns.splice(placedIndex, 0, column);
        }

        return [...orderedColumns, ...visible.filter((column) => column.hide === true)];
    });
}
